import React, { useState, useEffect, useCallback } from 'react';
import { Hourglass, Zap, BellRing, Ticket, PauseCircle, Send, Trash2, Loader2, Clock } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  notify_human: { Icon: BellRing, color: 'text-amber-700 dark:text-amber-400', label: 'Notificar humano' },
  create_ticket: { Icon: Ticket, color: 'text-brand-deep dark:text-brand-green', label: 'Crear ticket' },
  pause_noah: { Icon: PauseCircle, color: 'text-amber-700 dark:text-amber-400', label: 'Pausar Noah' },
};

export const DeferredEscalations = ({ campaignId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!campaignId) return;
    try {
      const res = await axios.get(`${API}/automations/deferred?campaign_id=${campaignId}&status=pending`);
      setItems(res.data.items || []);
    } catch {
      /* silencioso */
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const releaseOne = async (item) => {
    setBusy(item.id);
    try {
      const res = await axios.post(`${API}/automations/deferred/${item.id}/release`);
      toast.success(`Liberado: ${res.data.result}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo liberar');
    } finally {
      setBusy(null);
    }
  };

  const releaseAll = async () => {
    setBusy('all');
    try {
      const res = await axios.post(`${API}/automations/deferred/release-all?campaign_id=${campaignId}`);
      toast.success(`${res.data.released} aviso(s) liberado(s)`);
      load();
    } catch {
      toast.error('No se pudo liberar la cola');
    } finally {
      setBusy(null);
    }
  };

  const discard = async (item) => {
    try {
      await axios.delete(`${API}/automations/deferred/${item.id}`);
      toast.info('Aviso descartado');
      load();
    } catch {
      toast.error('No se pudo descartar');
    }
  };

  if (loading) return null;

  return (
    <Card className={cn('border-border/60', items.length > 0 && 'border-amber-500/40 bg-amber-500/5')}
      data-testid="deferred-escalations-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Hourglass className={cn('w-4 h-4', items.length ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground')} />
          <p className="text-sm font-semibold">Avisos en espera</p>
          <Badge variant="outline" data-testid="deferred-count"
            className={cn('text-[10px]', items.length
              ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30'
              : 'bg-muted/40 text-muted-foreground')}>
            {items.length}
          </Badge>
          {items.length > 0 && (
            <Button size="sm" className="ml-auto gap-1 bg-brand-copper"
              onClick={releaseAll} disabled={busy === 'all'} data-testid="deferred-release-all">
              {busy === 'all' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Liberar todo
            </Button>
          )}
        </div>

        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Nada retenido. Si Noah escala fuera del horario, el aviso se guarda aquí y salta solo al abrir el turno o cuando alguien se conecte.
          </p>
        ) : (
          <div className="space-y-2" data-testid="deferred-list">
            {items.map((item) => {
              const meta = ACTION_META[item.action?.type] || { Icon: Zap, color: 'text-brand-deep dark:text-brand-deep dark:text-brand-green', label: item.action?.type };
              return (
                <div key={item.id} data-testid={`deferred-item-${item.id}`}
                  className="p-2.5 rounded-xl bg-background/60 border border-border/50 flex flex-wrap items-center gap-2">
                  <meta.Icon className={cn('w-4 h-4 shrink-0', meta.color)} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">
                      {meta.label} · {item.rule_name}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {item.ctx?.customer?.name || 'Cliente'} · “{(item.ctx?.message?.text || '').slice(0, 60)}”
                    </p>
                    <p className="text-[10px] text-muted-foreground/80">
                      {item.reason} · {new Date(item.created_at).toLocaleString('es')}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => releaseOne(item)} disabled={busy === item.id}
                    data-testid={`deferred-release-${item.id}`} className="gap-1 h-8">
                    {busy === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Liberar ahora
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => discard(item)}
                    data-testid={`deferred-discard-${item.id}`} className="h-8 w-8">
                    <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
